import { getMessageDate, parseTimestampToDate } from './datetime.js';

export const EVALUATION_TIMEOUT_MS = 0;
export const EVALUATION_COMMENT_TIMEOUT_MS = 0;

const SATISFACTION_SURVEY_TRIGGER = 'satisfaction_survey';
const ACTIVE_EVALUATION_STATUSES = new Set([
  'pending',
  'awaiting_comment',
  'waiting_comment',
  'comment_pending',
]);
const TERMINAL_EVALUATION_STATUSES = new Set([
  'answered',
  'completed',
  'complete',
  'expired',
  'canceled',
  'cancelled',
]);

const normalizeToken = (value) => String(value ?? '').trim().toLowerCase();

const isFinalStatusValue = (value) => {
  const normalized = normalizeToken(value);
  return normalized === 'closed' || normalized === 'canceled';
};

const COMMENT_PENDING_EVALUATION_STATUSES = new Set([
  'awaiting_comment',
  'waiting_comment',
  'comment_pending',
]);

export const EVALUATION_COUNTDOWN_STATES = {
  RATING_PENDING: 'rating_pending',
  COMMENT_PENDING: 'comment_pending',
  COMPLETED: 'completed',
  EXPIRED: 'expired',
  INACTIVE: 'inactive',
};

export const getEvaluationWorkflowState = (slot) => {
  if (!slot || typeof slot !== 'object') return null;
  if (slot.workflow_state && typeof slot.workflow_state === 'object') return slot.workflow_state;
  if (slot.workflowState && typeof slot.workflowState === 'object') return slot.workflowState;
  return null;
};

export const getEvaluationStatus = (slot) => {
  const workflowState = getEvaluationWorkflowState(slot);
  return normalizeToken(
    workflowState?.evaluation_status ??
      workflowState?.evaluationStatus ??
      slot?.evaluation_status ??
      slot?.evaluationStatus
  );
};

export const getEvaluationTriggerType = (slot) => {
  const workflowState = getEvaluationWorkflowState(slot);
  return normalizeToken(
    workflowState?.trigger_type ??
      workflowState?.triggerType ??
      workflowState?.workflow_trigger_type ??
      workflowState?.workflowTriggerType ??
      slot?.trigger_type ??
      slot?.triggerType
  );
};

export const getEvaluationExpiresAtTimestampMs = (slot) => {
  const workflowState = getEvaluationWorkflowState(slot);
  const candidates = [
    workflowState?.evaluation_expires_at,
    workflowState?.evaluationExpiresAt,
    slot?.evaluation_expires_at,
    slot?.evaluationExpiresAt,
  ];
  for (const candidate of candidates) {
    const parsed = parseTimestampToDate(candidate);
    if (parsed) return parsed.getTime();
  }
  return 0;
};

export const getEvaluationStartedAtTimestampMs = (slot) => {
  const workflowState = getEvaluationWorkflowState(slot);
  const candidates = [
    workflowState?.workflow_timeout_started_at,
    workflowState?.workflowTimeoutStartedAt,
    workflowState?.timeout_started_at,
    workflowState?.timeoutStartedAt,
    workflowState?.waiting_started_at,
    workflowState?.waitingStartedAt,
    workflowState?.evaluation_started_at,
    workflowState?.evaluationStartedAt,
    workflowState?.evaluation_requested_at,
    workflowState?.evaluationRequestedAt,
    slot?.evaluation_started_at,
    slot?.evaluationStartedAt,
    slot?.evaluation_requested_at,
    slot?.evaluationRequestedAt,
  ];
  for (const candidate of candidates) {
    const parsed = parseTimestampToDate(candidate);
    if (parsed) return parsed.getTime();
  }
  return 0;
};

export const isConversationEvaluationActive = (slot, now = Date.now()) => {
  const state = getEvaluationCountdownStateName(slot, now);
  return (
    state === EVALUATION_COUNTDOWN_STATES.RATING_PENDING ||
    state === EVALUATION_COUNTDOWN_STATES.COMMENT_PENDING
  );
};

export const getEvaluationPendingStatus = (slot) => {
  if (!slot) return null;
  const classifiedState = getEvaluationCountdownStateName(slot, 0);
  if (classifiedState === EVALUATION_COUNTDOWN_STATES.COMMENT_PENDING) {
    return 'waiting_evaluation_comment';
  }
  if (classifiedState === EVALUATION_COUNTDOWN_STATES.RATING_PENDING) {
    return 'waiting_evaluation';
  }

  const evaluationStatus = getEvaluationStatus(slot);

  if (ACTIVE_EVALUATION_STATUSES.has(evaluationStatus)) {
    return COMMENT_PENDING_EVALUATION_STATUSES.has(evaluationStatus)
      ? 'waiting_evaluation_comment'
      : 'waiting_evaluation';
  }

  const handlingStatus = normalizeToken(slot?.handling_status);
  const status = normalizeToken(slot?.status);

  if (
    handlingStatus.includes('waiting_evaluation_comment') ||
    status.includes('waiting_evaluation_comment')
  ) {
    return 'waiting_evaluation_comment';
  }

  if (handlingStatus.includes('waiting_evaluation') || status.includes('waiting_evaluation')) {
    return 'waiting_evaluation';
  }

  return null;
};

export const getEvaluationCountdownStateName = (slot, now = Date.now()) => {
  if (!slot) return EVALUATION_COUNTDOWN_STATES.INACTIVE;

  const workflowState = getEvaluationWorkflowState(slot);
  const isSurveyWorkflow = getEvaluationTriggerType(slot) === SATISFACTION_SURVEY_TRIGGER;
  const evaluationStatus = getEvaluationStatus(slot);
  const expiresAtTs = getEvaluationExpiresAtTimestampMs(slot);
  const handlingStatus = normalizeToken(slot?.handling_status);
  const status = normalizeToken(slot?.status);
  const isWaitingEvaluation =
    handlingStatus.includes('waiting_evaluation') || status.includes('waiting_evaluation');
  if (expiresAtTs && expiresAtTs <= now) {
    return EVALUATION_COUNTDOWN_STATES.EXPIRED;
  }

  if (isSurveyWorkflow) {
    if (evaluationStatus === 'expired') return EVALUATION_COUNTDOWN_STATES.EXPIRED;
    if (TERMINAL_EVALUATION_STATUSES.has(evaluationStatus)) {
      return EVALUATION_COUNTDOWN_STATES.COMPLETED;
    }
    if (normalizeToken(slot?.status) !== 'closed') {
      return EVALUATION_COUNTDOWN_STATES.INACTIVE;
    }
    if (!isWaitingEvaluation) {
      return EVALUATION_COUNTDOWN_STATES.INACTIVE;
    }

    const hasRating = slot?.evaluation !== null && slot?.evaluation !== undefined;
    const hasComment = Boolean(String(slot?.evaluation_comment ?? slot?.evaluationComment ?? '').trim());
    if (COMMENT_PENDING_EVALUATION_STATUSES.has(evaluationStatus)) {
      return EVALUATION_COUNTDOWN_STATES.COMMENT_PENDING;
    }
    if (evaluationStatus === 'pending') {
      return hasRating && !hasComment
        ? EVALUATION_COUNTDOWN_STATES.COMMENT_PENDING
        : EVALUATION_COUNTDOWN_STATES.RATING_PENDING;
    }
    if (hasRating && !hasComment) {
      return EVALUATION_COUNTDOWN_STATES.COMMENT_PENDING;
    }
    if (workflowState?.pending_step_id != null || workflowState?.current_step_id != null) {
      return EVALUATION_COUNTDOWN_STATES.RATING_PENDING;
    }
    return EVALUATION_COUNTDOWN_STATES.INACTIVE;
  }

  if (expiresAtTs && isWaitingEvaluation) {
    return (
      handlingStatus.includes('waiting_evaluation_comment') ||
      status.includes('waiting_evaluation_comment')
    )
      ? EVALUATION_COUNTDOWN_STATES.COMMENT_PENDING
      : EVALUATION_COUNTDOWN_STATES.RATING_PENDING;
  }

  if (isFinalStatusValue(slot?.status) || isFinalStatusValue(slot?.handling_status)) {
    return EVALUATION_COUNTDOWN_STATES.INACTIVE;
  }

  if (
    handlingStatus.includes('waiting_evaluation_comment') ||
    status.includes('waiting_evaluation_comment')
  ) {
    return EVALUATION_COUNTDOWN_STATES.COMMENT_PENDING;
  }
  if (handlingStatus.includes('waiting_evaluation') || status.includes('waiting_evaluation')) {
    return EVALUATION_COUNTDOWN_STATES.RATING_PENDING;
  }

  return EVALUATION_COUNTDOWN_STATES.INACTIVE;
};

export const getEvaluationReferenceTimestampMs = (slot) => {
  if (!slot) return 0;

  const startedAt = getEvaluationStartedAtTimestampMs(slot);
  if (startedAt) return startedAt;

  const messages = Array.isArray(slot?.messages) ? slot.messages : [];
  if (messages.length > 0) {
    const lastMessage = messages[messages.length - 1];
    const lastMessageDate = getMessageDate(lastMessage);
    if (lastMessageDate) return lastMessageDate.getTime();
  }

  const candidates = [slot.last_message_at, slot.updated_at, slot.created_at];
  for (const value of candidates) {
    const parsed = parseTimestampToDate(value);
    if (parsed) return parsed.getTime();
  }

  return 0;
};

export const getEvaluationTimeoutMs = (pendingStatus) => {
  if (pendingStatus === 'waiting_evaluation_comment') {
    return EVALUATION_COMMENT_TIMEOUT_MS;
  }
  if (pendingStatus === 'waiting_evaluation') {
    return EVALUATION_TIMEOUT_MS;
  }
  return 0;
};

export const formatEvaluationRemainingTime = (remainingMs) => {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0 && seconds > 0) return `${minutes} min e ${seconds}s`;
  if (minutes > 0) return `${minutes} min`;
  return `${seconds}s`;
};

export const buildEvaluationCountdownState = ({
  slot = null,
  pendingStatus,
  referenceTs,
  expiresAtTs,
  now = Date.now(),
} = {}) => {
  const resolvedReferenceTs = referenceTs || getEvaluationReferenceTimestampMs(slot);
  const resolvedExpiresAtTs = expiresAtTs || getEvaluationExpiresAtTimestampMs(slot);
  if (resolvedExpiresAtTs) {
    return {
      timeoutMs: resolvedReferenceTs ? Math.max(0, resolvedExpiresAtTs - resolvedReferenceTs) : 0,
      remainingMs: Math.max(0, resolvedExpiresAtTs - now),
      expiresAtTs: resolvedExpiresAtTs,
    };
  }

  const timeoutMs = getEvaluationTimeoutMs(pendingStatus);
  if (!timeoutMs || !resolvedReferenceTs) return null;

  return {
    timeoutMs,
    remainingMs: Math.max(0, timeoutMs - Math.max(0, now - resolvedReferenceTs)),
  };
};

export const buildEvaluationCountdownLabel = ({
  slot = null,
  pendingStatus,
  referenceTs,
  expiresAtTs,
  now = Date.now(),
  waitingLabel = 'Aguardando avaliacao',
  commentLabel = 'Aguardando comentario',
} = {}) => {
  const info = getEvaluationCountdownInfo({
    slot,
    pendingStatus,
    referenceTs,
    expiresAtTs,
    now,
    waitingLabel,
    commentLabel,
  });
  return info?.label ?? null;
};

export const getEvaluationCountdownInfo = ({
  slot = null,
  pendingStatus,
  referenceTs,
  expiresAtTs,
  now = Date.now(),
  waitingLabel = 'Aguardando avaliacao',
  commentLabel = 'Aguardando comentario',
} = {}) => {
  const state = getEvaluationCountdownStateName(slot, now);
  if (
    state === EVALUATION_COUNTDOWN_STATES.COMPLETED ||
    state === EVALUATION_COUNTDOWN_STATES.EXPIRED ||
    state === EVALUATION_COUNTDOWN_STATES.INACTIVE
  ) {
    return null;
  }

  const resolvedPendingStatus =
    pendingStatus ||
    (state === EVALUATION_COUNTDOWN_STATES.COMMENT_PENDING
      ? 'waiting_evaluation_comment'
      : 'waiting_evaluation');
  const resolvedReferenceTs = referenceTs || getEvaluationReferenceTimestampMs(slot);
  const baseLabel =
    state === EVALUATION_COUNTDOWN_STATES.COMMENT_PENDING ||
    resolvedPendingStatus === 'waiting_evaluation_comment'
      ? commentLabel
      : state === EVALUATION_COUNTDOWN_STATES.RATING_PENDING ||
        resolvedPendingStatus === 'waiting_evaluation'
      ? waitingLabel
      : null;

  if (!baseLabel) return null;

  const resolvedExpiresAtTs = expiresAtTs || getEvaluationExpiresAtTimestampMs(slot);
  const countdownState = buildEvaluationCountdownState({
    slot,
    pendingStatus: resolvedPendingStatus,
    referenceTs: resolvedReferenceTs,
    expiresAtTs: resolvedExpiresAtTs,
    now,
  });

  if (!countdownState) return null;
  const remainingSeconds = Math.max(0, Math.ceil(countdownState.remainingMs / 1000));
  const base = {
    state,
    pendingStatus: resolvedPendingStatus,
    label: baseLabel,
    expiresAtTs: countdownState.expiresAtTs ?? resolvedExpiresAtTs ?? null,
    expiresAt: countdownState.expiresAtTs
      ? new Date(countdownState.expiresAtTs).toISOString()
      : null,
    remainingMs: countdownState.remainingMs,
    remainingSeconds,
    referenceTs: resolvedReferenceTs || 0,
  };
  if (countdownState.remainingMs > 0) {
    return {
      ...base,
      label: `${baseLabel} · encerra em ${formatEvaluationRemainingTime(countdownState.remainingMs)}`,
    };
  }

  return {
    ...base,
    label: `${baseLabel} · encerrando...`,
  };
};

export const buildEvaluationPendingWarningMessage = (slot, now = Date.now()) => {
  if (!slot) return null;
  const hasActiveWorkflowEvaluation = isConversationEvaluationActive(slot, now);
  if (
    !hasActiveWorkflowEvaluation &&
    (isFinalStatusValue(slot?.status) || isFinalStatusValue(slot?.handling_status))
  ) {
    return null;
  }

  const pendingStatus = getEvaluationPendingStatus(slot);
  if (!pendingStatus) return null;

  const countdownState = buildEvaluationCountdownState({
    slot,
    pendingStatus,
    referenceTs: getEvaluationReferenceTimestampMs(slot),
    now,
  });

  if (pendingStatus === 'waiting_evaluation_comment') {
    if (countdownState?.remainingMs > 0) {
      return `Este atendimento esta aguardando comentario da avaliacao. Aguarde cerca de ${formatEvaluationRemainingTime(countdownState.remainingMs)} para iniciar uma nova conversa, ou ate o contato comentar.`;
    }

    return 'Este atendimento esta aguardando comentario da avaliacao. Aguarde o periodo de avaliacao encerrar ou o contato comentar para iniciar uma nova conversa.';
  }

  if (pendingStatus === 'waiting_evaluation') {
    if (countdownState?.remainingMs > 0) {
      return `Este atendimento esta aguardando a avaliacao do contato. Aguarde cerca de ${formatEvaluationRemainingTime(countdownState.remainingMs)} para iniciar uma nova conversa, ou ate o contato avaliar/comentar.`;
    }

    return 'Este atendimento esta aguardando a avaliacao do contato. Aguarde o periodo de avaliacao encerrar ou o contato avaliar/comentar para iniciar uma nova conversa.';
  }

  return 'Este atendimento esta aguardando a avaliacao do contato. Aguarde o contato avaliar/comentar para iniciar uma nova conversa.';
};
